import React, { useReducer, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ApolloQueryResult } from '@apollo/client';
import { equals } from 'ramda';
import { Heading3, Heading4 } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import removeTypenames from '~/util/removeTypenames';
import {
  Lfa_Account_Status,
  useUpdateLfaAccountSettingsMutation,
  type GetLfaAccountSettingsQuery,
  type GetLfaConversionDataQuery,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Divider from '~/components/atom/Divider';
import { TEXT } from './constants';
import settingsReducer, {
  type UpdateSettingsState,
} from './reducers/settingsReducer';
import useSettingsValidation from './hooks/useSettingsValidation';
import CallToActionBlock from '~/components/organism/CallToActionBlock';
import SelectServicesSection from './components/SelectServicesSection';
import AccountDescriptionSection from './components/AccountDescriptionSection';
import CallbackPhoneNumberSection from './components/CallbackPhoneNumberSection';
import RulesSection from './components/RulesSection';
import UserOfficeSelectionSection from './components/UserOfficeSelectionSection';
import FlynkSettingsSection from './components/FlynkSettingsSection';
import FloatingSaveBar from '~/components/molecule/FloatingSaveBar';
import getUpdatedSettingsInput from './utils/getUpdatedSettingsInput';
import hasValue from '~/util/hasValue';

type Props = {
  queryData: NonNullable<GetLfaAccountSettingsQuery['getLfaAccountSettings']>;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  refetch: () => Promise<ApolloQueryResult<GetLfaAccountSettingsQuery>>;
};

const UpdateForm: React.FCC<Props> = ({ queryData, allServices, refetch }) => {
  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { description, services, rules, flynck, callbackPhoneNumber } =
    queryData;

  const { notOfficeId, notTags, notUserId, minLeadscore } = rules;

  const initialSettings: UpdateSettingsState = removeTypenames({
    description: description || null,
    services,
    rules: {
      notOfficeId,
      notTags,
      notUserId,
      minLeadscore: minLeadscore.toString(),
    },
    flynck: flynck || null,
    callbackPhoneNumber: callbackPhoneNumber || null,
  });

  const [updateSettings, dispatch] = useReducer(
    settingsReducer,
    initialSettings,
  );

  const [tagInputValue, setTagInputValue] = useState('');

  const [updateLfaAccountSettings, { loading: updateLoading }] =
    useUpdateLfaAccountSettingsMutation();

  const { errors, hasErrors, setFieldTouched, clearAllErrors } =
    useSettingsValidation({
      updateSettings,
    });

  const onUpdate = async () => {
    const updatedFields = getUpdatedSettingsInput(updateSettings);

    void updateLfaAccountSettings({
      variables: {
        accountId,
        update: {
          UpdateSettings: updatedFields,
        },
      },
    }).then(async ({ data, errors }) => {
      if (data && !errors) {
        clearAllErrors();
        await refetch();
        return addToast([formatToastMessage(TEXT.successMessage, 'success')]);
      }
      return addToast([formatToastMessage(TEXT.errorMessage, 'danger')]);
    });
  };

  const isActive = queryData.status === Lfa_Account_Status.Active;
  const isInactive = queryData.status == Lfa_Account_Status.Inactive;

  const saveButtonDisabled = isActive && (updateLoading || hasErrors);

  const hasChanges = !equals(initialSettings, updateSettings);
  const saveBarError = Object.values(errors).find(error =>
    hasValue(error),
  )?.[0];

  return (
    <>
      {isInactive && (
        <CallToActionBlock
          header={TEXT.accountNotActiveHeader}
          description={TEXT.accountNotActiveWarning}
          icon={{
            name: 'exclamation',
            background: { group: 'warning' },
          }}
          margin={[null, null, 'base']}
        />
      )}

      <Container
        direction="column"
        width="100%"
        gap="l"
        $disabled={updateLoading || isInactive}
      >
        <Heading3 size="l" margin={[null]}>
          {TEXT.settingsHeader}
        </Heading3>

        <AccountDescriptionSection
          updateSettings={updateSettings}
          dispatch={dispatch}
        />

        <CallbackPhoneNumberSection
          updateSettings={updateSettings}
          phoneNumberErrors={errors.phoneNumber}
          dispatch={dispatch}
          setFieldTouched={setFieldTouched}
        />

        <SelectServicesSection
          allServices={removeTypenames(allServices)}
          updateSettings={updateSettings}
          dispatch={dispatch}
          setFieldTouched={setFieldTouched}
        />

        <Heading4 size="m">{TEXT.rulesHeader}</Heading4>

        <RulesSection
          updateSettings={updateSettings}
          dispatch={dispatch}
          leadScoreErrors={errors.leadscore}
          tagInputValue={tagInputValue}
          setTagInputValue={setTagInputValue}
          setFieldTouched={setFieldTouched}
        />

        <Divider margin={['s', null]} />

        <UserOfficeSelectionSection
          updateSettings={updateSettings}
          dispatch={dispatch}
        />

        <Divider margin={['s', null]} />

        <FlynkSettingsSection
          setFieldTouched={setFieldTouched}
          flynckErrors={errors.flynck}
          updateSettings={updateSettings}
          dispatch={dispatch}
        />

        <FloatingSaveBar
          visible={hasChanges}
          anchorRef={contentRef}
          errorMessage={saveBarError}
          actionProps={{
            onCancel: () => {
              clearAllErrors();
              dispatch({ type: 'RESET', payload: initialSettings });
            },
            onSave: onUpdate,
            loading: updateLoading,
            disabled: saveButtonDisabled,
          }}
        />
      </Container>
    </>
  );
};

const Container = styled(JustificationContainer)<{ $disabled: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export default UpdateForm;
